import React, { Component } from 'react';
import { Row, Col, Tabs, Tag, Icon, Divider, Button } from 'antd';
import { Chart, Geom, Axis, Tooltip, Legend, Coord } from 'bizcharts';
import { DataView } from '@antv/data-set';
import moment from 'moment';
import GenericModal from '../../components/GenericModal';
import { Job, OutputType, ProcessType, User } from '../../JmReact';
if (window.location.search.indexOf("autoIphone=1") > -1) {
    import("../../dashboardOrders.css").then(() => { });
}
const { TabPane } = Tabs;

// ─── Constants ─────────────────────────────────────────────────────────────────
const DARK_BG = '#0f172a';
const CARD_BG = 'rgba(255,255,255,0.08)';
const CARD_BORDER = '1px solid rgba(255,255,255,0.15)';
const CARD_RADIUS = 18;
const TEXT_MUTED = '#94a3b8';
const TEXT_LIGHT = '#e2e8f0';

const COLORS = {
  green: '#22c55e',
  blue: '#38bdf8',
  orange: '#fb923c',
  purple: '#c084fc',
  red: '#f43f5e',
};

// Status: 2=בוטל | 6=סופק | 7=מאושר
const STATUS_CFG = {
  2: { label: 'בוטל', color: COLORS.red, icon: 'close-circle' },
  6: { label: 'סופק', color: COLORS.green, icon: 'check-circle' },
  7: { label: 'מאושר', color: COLORS.blue, icon: 'clock-circle' },
};

// ─── Pure helpers ───────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  return `\u20AA${Number(val || 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
function fmtTime(dateStr) {
  return dateStr ? moment(dateStr).format('HH:mm') : '\u2014';
}

// ─── computeStats ───────────────────────────────────────────────────────────────
/**
 * Pure aggregation — receives the raw orders array and returns
 * all derived summaries. No side-effects.
 *
 * @param  {Array} orders  - objects matching the JSON schema
 * @returns {Object} { kpi, byTable, maxTableRev, byStatus, byHour,
 *                     donutData, openTables, sorted, insights }
 */
function computeStats(orders = []) {
  const active = orders.filter(o => o.Status !== 2);
  const delivered = orders.filter(o => o.Status === 6);
  const cancelled = orders.filter(o => o.Status === 2);
  const pending = orders.filter(o => o.Status === 7);

  const totalRevenue = active.reduce((s, o) => s + (o.Total || 0), 0);
  const totalDiners = active.reduce((s, o) => s + (parseInt(o.DinersCount, 10) || 0), 0);
  const avgPerOrder = active.length ? totalRevenue / active.length : 0;
  const avgPerDiner = totalDiners ? totalRevenue / totalDiners : 0;

  // byTable
  const tableMap = {};
  orders.forEach(o => {
    const key = o.TableName ? o.TableName.trim() : `Table ${o.TableId}`;
    if (!tableMap[key]) tableMap[key] = {
      tableName: key, tableId: o.TableId,
      activeRevenue: 0, activeCount: 0, cancelledCount: 0,
      diners: 0, orders: [], firstOrder: null,
    };
    tableMap[key].orders.push(o);
    if (!tableMap[key].firstOrder || moment(o.DateTime) < moment(tableMap[key].firstOrder))
      tableMap[key].firstOrder = o.DateTime;
    if (o.Status !== 2) {
      tableMap[key].activeRevenue += o.Total || 0;
      tableMap[key].activeCount++;
      tableMap[key].diners += parseInt(o.DinersCount, 10) || 0;
    } else {
      tableMap[key].cancelledCount++;
    }
  });
  const byTable = Object.values(tableMap).sort((a, b) => b.activeRevenue - a.activeRevenue);
  const maxTableRev = byTable.length ? byTable[0].activeRevenue : 1;

  // openTables (status 7)
  const openTableMap = {};
  orders.filter(o => o.Status === 7).forEach(o => {
    const key = o.TableName ? o.TableName.trim() : `Table ${o.TableId}`;
    if (!openTableMap[key]) openTableMap[key] = {
      tableName: key, tableId: o.TableId,
      total: 0, diners: 0, orderNum: o.OrderNum, openSince: o.DateTime,
    };
    openTableMap[key].total += o.Total || 0;
    openTableMap[key].diners += parseInt(o.DinersCount, 10) || 0;
  });
  const openTables = Object.values(openTableMap);

  // byStatus
  const statusMap = {};
  orders.forEach(o => {
    const cfg = STATUS_CFG[o.Status] || { label: String(o.Status) };
    const key = cfg.label;
    if (!statusMap[key]) statusMap[key] = { status: key, count: 0, revenue: 0 };
    statusMap[key].count++;
    if (o.Status !== 2) statusMap[key].revenue += o.Total || 0;
  });
  const byStatus = Object.values(statusMap);

  // byHour
  const hourMap = {};
  active.forEach(o => {
    const hour = o.DateTime ? moment(o.DateTime).format('HH') + ':00' : '??';
    if (!hourMap[hour]) hourMap[hour] = { hour, revenue: 0, count: 0 };
    hourMap[hour].revenue += o.Total || 0;
    hourMap[hour].count++;
  });
  const byHour = Object.values(hourMap).sort((a, b) => a.hour.localeCompare(b.hour));

  // donut data
  const donutData = [
    { type: 'סופק', value: delivered.length },
    { type: 'בוטל', value: cancelled.length },
    { type: 'מאושר', value: pending.length },
  ].filter(d => d.value > 0);

  // insights
  const topTable = byTable[0] || null;
  const overtime = openTables.filter(t =>
    t.openSince && moment().diff(moment(t.openSince), 'minutes') > 45
  );
  const insights = {
    topTable,
    overtime,
    cancelRate: orders.length ? Math.round((cancelled.length / orders.length) * 100) : 0,
    avgPerOrder,
    avgPerDiner,
  };

  const sorted = [...orders].sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));

  return {
    kpi: {
      totalRevenue, totalDiners, avgPerOrder, avgPerDiner,
      totalOrders: orders.length, activeOrders: active.length,
      deliveredCount: delivered.length, cancelledCount: cancelled.length,
      pendingCount: pending.length,
      tableCount: byTable.length, openTableCount: openTables.length,
    },
    byTable, maxTableRev, byStatus, byHour, donutData, openTables, sorted, insights,
  };
}

// ─── DarkCard ───────────────────────────────────────────────────────────────────
function DarkCard({ children, style }) {
  return (
    <div style={{
      background: CARD_BG,
      backdropFilter: 'blur(12px)',
      border: CARD_BORDER,
      borderRadius: CARD_RADIUS,
      padding: 16,
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── KpiGrid ────────────────────────────────────────────────────────────────────
function KpiGrid({ kpi }) {
  const items = [
    { label: 'סה"כ הכנסות', value: fmtCurrency(kpi.totalRevenue), color: COLORS.green, icon: 'dollar' },
    { label: 'הזמנות סופקו', value: `${kpi.deliveredCount} / ${kpi.totalOrders}`, color: COLORS.blue, icon: 'check-circle' },
    { label: 'כמות הזמנות', value: kpi.activeOrders, color: COLORS.orange, icon: 'unordered-list' },
    { label: 'כמות סועדים', value: kpi.totalDiners, color: COLORS.purple, icon: 'team' },
    { label: 'ממוצע להזמנה', value: fmtCurrency(kpi.avgPerOrder), color: TEXT_LIGHT, icon: 'calculator' },
    { label: 'ממוצע לסועד', value: fmtCurrency(kpi.avgPerDiner), color: TEXT_LIGHT, icon: 'user' },
    { label: 'שולחנות פעילים', value: kpi.tableCount, color: COLORS.green, icon: 'shop' },
    { label: 'הזמנות בוטלו', value: kpi.cancelledCount, color: COLORS.red, icon: 'close-circle' },
  ];
  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16, direction: 'rtl' }}>
      {items.map((item, i) => (
        <Col xs={12} sm={12} md={6} key={i}>
          <DarkCard style={{ height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Icon type={item.icon} style={{ color: item.color, fontSize: 16 }} />
              <span style={{ fontSize: 12, color: TEXT_MUTED }}>{item.label}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: item.color, lineHeight: 1.1 }}>
              {item.value}
            </div>
          </DarkCard>
        </Col>
      ))}
    </Row>
  );
}

// ─── DonutChart ─────────────────────────────────────────────────────────────────
function DonutChart({ donutData }) {
  const dv = new DataView();
  dv.source(donutData).transform({ type: 'percent', field: 'value', dimension: 'type', as: 'percent' });
  return (
    <Chart
      height={220}
      data={dv}
      padding={[20, 140, 20, 20]}   // ← padding ימין גדול יותר למקום ה-legend
      forceFit
    >
      <Coord type="theta" radius={0.75} innerRadius={0.55} />
      <Tooltip showTitle={false} />
      <Legend
        position="right-center"     // ← שינוי מ-"right" ל-"right-center"
        offsetX={-10}
        textStyle={{ fill: TEXT_LIGHT, fontSize: 12 }}
        itemMarginBottom={10}
      />
      <Geom
        type="intervalStack"
        position="percent"
        color={['type', [COLORS.green, COLORS.red, COLORS.blue]]}
        tooltip={['type*value', (type, value) => ({ name: type, value })]}
        style={{ lineWidth: 0 }}
      />
    </Chart>
  );

}

// ─── RevenueBarChart ────────────────────────────────────────────────────────────
function RevenueBarChart({ byTable }) {
  const data = byTable.map(t => ({ table: t.tableName, revenue: t.activeRevenue }));
  return (
    <Chart height={220} data={data} padding={[20, 20, 60, 60]} forceFit>
      <Axis name="table"
        label={{ textStyle: { fill: TEXT_MUTED, fontSize: 11 } }}
        line={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={null} />
      <Axis name="revenue"
        label={{ formatter: v => `\u20AA${v}`, textStyle: { fill: TEXT_MUTED, fontSize: 11 } }}
        grid={{ lineStyle: { stroke: 'rgba(255,255,255,0.08)' } }} />
      <Tooltip />
      <Geom type="interval" position="table*revenue" color={COLORS.blue} shape="border-radius"
        tooltip={['table*revenue', (table, revenue) => ({ name: table, value: fmtCurrency(revenue) })]} />
    </Chart>
  );
}

// ─── HourlyLineChart ────────────────────────────────────────────────────────────
function HourlyLineChart({ byHour }) {
  return (
    <Chart height={220} data={byHour} padding={[20, 20, 40, 60]} forceFit>
      <Axis name="hour"
        label={{ textStyle: { fill: TEXT_MUTED, fontSize: 11 } }}
        line={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={null} />
      <Axis name="revenue"
        label={{ formatter: v => `\u20AA${v}`, textStyle: { fill: TEXT_MUTED, fontSize: 11 } }}
        grid={{ lineStyle: { stroke: 'rgba(255,255,255,0.08)' } }} />
      <Tooltip />
      <Geom type="area" position="hour*revenue" color={COLORS.purple} shape="smooth"
        style={{ fillOpacity: 0.25 }} />
      <Geom type="line" position="hour*revenue" color={COLORS.purple} shape="smooth" size={2}
        tooltip={['hour*revenue', (hour, revenue) => ({ name: hour, value: fmtCurrency(revenue) })]} />
    </Chart>
  );
}

// ─── StatusBarChart ─────────────────────────────────────────────────────────────
function StatusBarChart({ byStatus }) {
  return (
    <Chart height={200} data={byStatus} padding={[20, 20, 40, 40]} forceFit>
      <Axis name="status"
        label={{ textStyle: { fill: TEXT_MUTED, fontSize: 12 } }}
        line={null} tickLine={null} />
      <Axis name="count"
        label={{ textStyle: { fill: TEXT_MUTED, fontSize: 11 } }}
        grid={{ lineStyle: { stroke: 'rgba(255,255,255,0.08)' } }} />
      <Tooltip />
      <Geom type="interval" position="status*count" shape="border-radius"
        color={['status', [COLORS.green, COLORS.red, COLORS.blue]]}
        tooltip={['status*count', (status, count) => ({ name: status, value: count })]} />
    </Chart>
  );
}

// ─── OpenTablesList ─────────────────────────────────────────────────────────────
function OpenTablesList({ openTables }) {
  if (!openTables.length)
    return <div style={{ color: TEXT_MUTED, textAlign: 'center', padding: '20px 0' }}>אין שולחנות פתוחים כרגע</div>;

  return (
    <div>
      {openTables.map((t, i) => {
        const minutesOpen = t.openSince ? moment().diff(moment(t.openSince), 'minutes') : 0;
        const isOvertime = minutesOpen > 45;
        const pct = Math.min(Math.round((minutesOpen / 90) * 100), 100);
        return (
          <DarkCard key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{t.tableName}</div>
                <div style={{ fontSize: 13, color: TEXT_MUTED, marginTop: 4, lineHeight: 1.7 }}>
                  {`\u05d4\u05d6\u05de\u05e0\u05d4 ${t.orderNum}`}
                  {t.diners > 0 && ` \u00b7 ${t.diners} \u05e1\u05d5\u05e2\u05d3\u05d9\u05dd`}
                  {t.openSince && ` \u00b7 ${minutesOpen} \u05d3\u05e7\u05d5\u05ea`}
                </div>
                <span style={{
                  display: 'inline-block', marginTop: 8, padding: '3px 12px',
                  borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: isOvertime ? COLORS.red : '#1d4ed8', color: '#fff',
                }}>
                  {isOvertime ? 'חריגה בזמן' : 'פתוח'}
                </span>
                <div style={{ height: 10, background: 'rgba(255,255,255,0.12)', borderRadius: 20, overflow: 'hidden', marginTop: 10 }}>
                  <div style={{
                    width: `${pct}%`, height: '100%', borderRadius: 20, transition: 'width 0.6s ease',
                    background: isOvertime
                      ? `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.red})`
                      : `linear-gradient(90deg, ${COLORS.green}, ${COLORS.blue})`,
                  }} />
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginRight: 16, paddingTop: 4 }}>
                {fmtCurrency(t.total)}
              </div>
            </div>
          </DarkCard>
        );
      })}
    </div>
  );
}

// ─── QuickInsights ──────────────────────────────────────────────────────────────
function QuickInsights({ insights, kpi }) {
  const lines = [];
  if (kpi.openTableCount > 0)
    lines.push(<span>כרגע יש <b style={{ color: COLORS.green }}>{kpi.openTableCount}</b> שולחנות פתוחים.</span>);
  if (insights.topTable)
    lines.push(<span>השולחן המוביל: <b style={{ color: COLORS.blue }}>{insights.topTable.tableName}</b> — {fmtCurrency(insights.topTable.activeRevenue)}.</span>);
  if (insights.overtime.length > 0)
    lines.push(<span style={{ color: COLORS.red }}><Icon type="warning" /> {insights.overtime.map(t => t.tableName).join(', ')} — פתוח מעל 45 דקות.</span>);
  if (insights.cancelRate > 0)
    lines.push(<span>שיעור ביטולים: <b>{insights.cancelRate}%</b> מסך ההזמנות.</span>);
  lines.push(<span>ממוצע להזמנה: <b>{fmtCurrency(insights.avgPerOrder)}</b> · ממוצע לסועד: <b>{fmtCurrency(insights.avgPerDiner)}</b>.</span>);

  return (
    <DarkCard>
      <div style={{ fontSize: 15, lineHeight: 2, color: TEXT_LIGHT, direction: 'rtl' }}>
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </DarkCard>
  );
}

// ─── TableSummaryTab ────────────────────────────────────────────────────────────
function TableSummaryTab({ byTable, maxTableRev }) {
  return (
    <div style={{ direction: 'rtl' }}>
      {byTable.map(t => (
        <div key={t.tableName} style={{ marginBottom: 16 }}>
          {/* <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{t.tableName}</span>
              <span style={{ fontSize: 12, color: TEXT_MUTED, marginRight: 8 }}>
                {t.activeCount} הזמנות
                {t.cancelledCount > 0 && ` · ${t.cancelledCount} בוטלו`}
                {t.diners > 0 && ` · ${t.diners} סועדים`}
              </span>
            </div>
            <span style={{ fontWeight: 700, color: '#fff' }}>{fmtCurrency(t.activeRevenue)}</span>
          </div> */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#fff', minWidth: 80 }}>
              {t.tableName}
            </span>
            <span style={{ fontSize: 12, color: TEXT_MUTED, flex: 1, padding: '0 8px' }}>
              {t.activeCount} הזמנות
              {t.cancelledCount > 0 && ` · ${t.cancelledCount} בוטלו`}
              {t.diners > 0 && ` · ${t.diners} סועדים`}
            </span>
            <span style={{ fontWeight: 700, color: '#fff' }}>{fmtCurrency(t.activeRevenue)}</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 20, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round((t.activeRevenue / maxTableRev) * 100)}%`,
              height: '100%', borderRadius: 20,
              background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.blue})`,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── TimelineTab ────────────────────────────────────────────────────────────────
function TimelineTab({ sorted }) {
  return (
    <div style={{ direction: 'rtl' }}>
      {sorted.map((o, i) => {
        const cfg = STATUS_CFG[o.Status] || { label: String(o.Status), color: TEXT_MUTED, icon: 'info-circle' };
        return (
          <div key={o.OrderID}>
            <div style={{ padding: '10px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: TEXT_MUTED, fontSize: 12 }}>{fmtTime(o.DateTime)}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{o.TableName && o.TableName.trim()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: '#fff' }}>{fmtCurrency(o.Total)}</span>
                  <Tag color={cfg.color} style={{ borderRadius: 12, margin: 0 }}>
                    <Icon type={cfg.icon} style={{ marginLeft: 4 }} />{cfg.label}
                  </Tag>
                </div>
              </div>
              <div style={{ fontSize: 11, color: TEXT_MUTED }}>
                {o.OrderNum}{o.DinersCount ? ` · ${o.DinersCount} סועדים` : ''}
              </div>
            </div>
            {i < sorted.length - 1 && <Divider style={{ margin: 0, borderColor: 'rgba(255,255,255,0.08)' }} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── AllOrdersTab ───────────────────────────────────────────────────────────────
function AllOrdersTab({ sorted }) {
  const isNarrow = window.innerWidth < 600;

  if (isNarrow) {
    return (
      <div style={{ direction: 'rtl' }}>
        {sorted.map(o => {
          const cfg = STATUS_CFG[o.Status] || { label: String(o.Status), color: TEXT_MUTED };
          return (
            <div key={o.OrderID} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: '#fff' }}>{o.TableName && o.TableName.trim()}</span>
                <span style={{ fontWeight: 700, color: '#fff' }}>{fmtCurrency(o.Total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: TEXT_MUTED }}>
                  {fmtTime(o.DateTime)} · {o.OrderNum}{o.DinersCount ? ` · ${o.DinersCount} סועדים` : ''}
                </span>
                <Tag color={cfg.color} style={{ borderRadius: 12, margin: 0 }}>
                  {cfg.label}
                </Tag>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', direction: 'rtl' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: TEXT_LIGHT, tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            {['שעה', 'שולחן', 'מס׳ הזמנה', 'סועדים', 'סכום', 'סטטוס'].map(h => (
              <th key={h} style={{ padding: '10px 12px', textAlign: 'right', color: TEXT_MUTED, fontWeight: 500 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(o => {
            const cfg = STATUS_CFG[o.Status] || { label: String(o.Status), color: TEXT_MUTED };
            return (
              <tr key={o.OrderID} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={{ padding: '10px 12px' }}>{fmtTime(o.DateTime)}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }}>{o.TableName && o.TableName.trim()}</td>
                <td style={{ padding: '10px 12px', color: TEXT_MUTED, fontSize: 11 }}>{o.OrderNum}</td>
                <td style={{ padding: '10px 12px' }}>{o.DinersCount || '—'}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }}>{fmtCurrency(o.Total)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <Tag color={cfg.color} style={{ borderRadius: 12 }}>
                    <Icon type={cfg.icon} style={{ marginLeft: 4 }} />{cfg.label}
                  </Tag>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── MAIN CLASS COMPONENT ───────────────────────────────────────────────────────
/**
 * DinersDashboard — React 16 class component
 *
 * Compatible versions:
 *   antd          ^3.26.20   (Icon, Tabs/TabPane, Tag, Row/Col, Divider)
 *   bizcharts     ^3.5.10    (Chart, Geom, Axis, Tooltip, Legend, Coord)
 *   @antv/data-set ^0.10.2   (DataView)
 *   moment        ^2.29.4
 *
 * Props:
 *   orders   {Array}   — raw order objects (JSON schema as specified)
 *   title    {string}  — optional header title
 *   subtitle {string}  — optional header subtitle
 *
 * Usage:
 *   <DinersDashboard orders={myOrdersArray} />
 */
class DinersDashboard extends Component {
  static defaultProps = {
    title: 'דאשבורד מנהל מסעדה',
    subtitle: 'מצב חי של שולחנות, הזמנות וסועדים',
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: '1',
      orders: []
    };
  }

  apiOrders = () => {
    const { posList, fromDatetime, toDatetime } = this.props;
    let user = new User(this.props.user.token, this.props.user.companyCode);
    let job = new Job(user, "get_diners_orders", OutputType.OUTPUT_TYPE_DATA, ProcessType.PROCESS_TYPE_SYNC);
    job.setInput({ posList, fromDatetime, toDatetime });
    job.send("/cgi-bin/CashOnTab", ob => {
      this.setState({
        orders: ob.data ? ob.data.split("\r").map(x => {
          const [OrderID, OrderNum, DateTime, RequieredDateTime, TableId, TableName, DinersCount, Status, StatusName, Total] = x.split("\f");
          return { OrderID, OrderNum, DateTime, RequieredDateTime, TableId, TableName, DinersCount, Status, StatusName, Total }
        }) : []
      });
    }, (e) => { console.error(e) });
  }

  render() {
    const { title, subtitle } = this.props;
    const { activeTab, orders } = this.state;

    const {
      kpi, byTable, maxTableRev,
      byStatus, byHour, donutData,
      openTables, sorted, insights,
    } = computeStats(orders);

    const sectionTitle = text => (
      <div style={{ margin: '24px 4px 12px', fontSize: 18, fontWeight: 700, color: '#fff' }}>
        {text}
      </div>
    );

    return (<div style={{ textAlign: "center", marginBottom: 24, display: 'inline-block', verticalAlign: 'text-top' }}>
      <Button onClick={() => { this.setState({ modalOpen: true }, this.apiOrders) }}>
        <Icon type={"coffee"} style={{ marginLeft: 5 }} />
        סיכום סועדים
      </Button>
      <GenericModal
        title="סיכום סועדים"
        visible={this.state.modalOpen}
        onCancel={() => this.setState({ modalOpen: false })}
      >



        <div style={{ background: DARK_BG, minHeight: '100vh', color: '#fff', direction: 'rtl' }}>

          {/* Header */}
          <div style={{
            padding: '22px 18px 48px',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed, #ec4899)',
            borderBottomLeftRadius: 28, borderBottomRightRadius: 28,
          }}>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{title}</div>
            <div style={{ marginTop: 8, opacity: 0.9, fontSize: 14 }}>{subtitle}</div>
          </div>

          <div style={{ padding: '0 14px 40px', marginTop: 10 }}>

            {/* KPI */}
            <KpiGrid kpi={kpi} />

            {/* Tabs: summary / timeline / all orders */}
            <DarkCard style={{ marginBottom: 20 }}>
              <Tabs
                activeKey={activeTab}
                onChange={key => this.setState({ activeTab: key })}
                tabBarStyle={{ borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 16, color: TEXT_LIGHT }}
              >
                <TabPane tab={<span><Icon type="bar-chart" /> ריכוז שולחן</span>} key="1">
                  <TableSummaryTab byTable={byTable} maxTableRev={maxTableRev} />
                </TabPane>
                <TabPane tab={<span><Icon type="clock-circle" /> ציר זמן</span>} key="2">
                  <TimelineTab sorted={sorted} />
                </TabPane>
                <TabPane tab={<span><Icon type="unordered-list" /> כל ההזמנות</span>} key="3">
                  <AllOrdersTab sorted={sorted} />
                </TabPane>
              </Tabs>
            </DarkCard>

            {/* Charts */}
            {sectionTitle('חלוקת סטטוס הזמנות')}
            <DarkCard style={{ marginBottom: 20 }}>
              <DonutChart donutData={donutData} />
            </DarkCard>

            {sectionTitle('הכנסות לפי שולחן')}
            <DarkCard style={{ marginBottom: 20 }}>
              <RevenueBarChart byTable={byTable} />
            </DarkCard>

            {sectionTitle('הכנסות לפי שעה')}
            <DarkCard style={{ marginBottom: 20 }}>
              <HourlyLineChart byHour={byHour} />
            </DarkCard>

            {sectionTitle('סטטוס הזמנות')}
            <DarkCard style={{ marginBottom: 20 }}>
              <StatusBarChart byStatus={byStatus} />
            </DarkCard>

            {/* Open tables */}
            {sectionTitle('שולחנות פתוחים')}
            <OpenTablesList openTables={openTables} />

            {/* Insights */}
            {sectionTitle('תובנות מהירות')}
            <QuickInsights insights={insights} kpi={kpi} />

          </div>
        </div>

      </GenericModal>
    </div>
    );
  }
}

export default DinersDashboard;
